import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { SlidersHorizontal, Plus, X, Pencil, Check, Loader2, AlertTriangle } from "lucide-react";

const slug = (s) => String(s || "").replace(/[^a-zA-Z0-9]+/g, "-");

/**
 * PART 6.2 / 6.3 — admin controls behind the project's vertical graphs.
 *  • Add / remove / rename the vertical LIST (persists to the global taxonomy).
 *  • Edit the PLANNED BUDGET per vertical for THIS project.
 * Spend always stays auto-populated from invoices (read-only here).
 */
export function ProjectVerticalsDialog({ project, rows = [], trigger, onChanged }) {
  const { money } = useCurrency();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState({});          // vertical -> planned amount (string)
  const [orig, setOrig] = useState({});
  const [newVert, setNewVert] = useState("");
  const [rename, setRename] = useState(null);     // { old, value }
  const [reassign, setReassign] = useState(null); // { name, count, options }
  const [reassignTo, setReassignTo] = useState("");
  const [confirm, setConfirm] = useState(null);   // { type: 'rename'|'remove', name, to? }

  // Only real (taxonomy) verticals are manageable; reconcile-only extras are shown read-only.
  const taxRows = rows.filter((r) => r.in_taxonomy);
  const extraRows = rows.filter((r) => !r.in_taxonomy && (r.spend > 0));
  const vertCount = (name) => (rows.find((r) => r.vertical === name)?.count) || 0;

  const init = () => {
    const m = {};
    taxRows.forEach((r) => { m[r.vertical] = String(Math.round(r.budget || 0)); });
    setPlan(m); setOrig(m);
  };
  useEffect(() => { if (open) init(); /* eslint-disable-next-line */ }, [open]);

  const changed = useMemo(
    () => Object.keys(plan).filter((v) => (parseFloat(plan[v]) || 0) !== (parseFloat(orig[v]) || 0)),
    [plan, orig]
  );

  const savePlanned = async () => {
    if (changed.length === 0) { toast.info("No budget changes"); return; }
    setBusy(true);
    try {
      for (const v of changed) {
        await api.savePlannedBudget({ project, vertical: v, amount: parseFloat(plan[v]) || 0, reason: "project planned budget" });
      }
      toast.success(`Saved ${changed.length} planned budget${changed.length > 1 ? "s" : ""}`);
      setOrig({ ...plan });
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save planned budgets");
    } finally { setBusy(false); }
  };

  const addVertical = async () => {
    const name = newVert.trim();
    if (!name) return;
    if (taxRows.some((r) => r.vertical === name)) { toast.error(`"${name}" already exists`); return; }
    setBusy(true);
    try {
      await api.addVertical({ name, spend_type: "Project", label: name });
      toast.success(`Added vertical "${name}"`);
      setNewVert("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add vertical");
    } finally { setBusy(false); }
  };

  const doRename = async () => {
    const old = rename.old; const nn = (rename.value || "").trim();
    if (!nn || nn === old) { setRename(null); return; }
    setBusy(true);
    try {
      const res = await api.renameVertical(old, nn);
      toast.success(`Renamed "${old}" → "${nn}"${res.invoices_retagged ? ` · ${res.invoices_retagged} invoice(s) re-tagged` : ""}`);
      setRename(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename vertical");
    } finally { setBusy(false); }
  };

  const doRemove = async (name, target = null) => {
    setBusy(true);
    try {
      const res = await api.removeVertical(name, target);
      toast.success(target ? `Removed "${name}" · reassigned ${res.reassigned} to "${target}"` : `Removed "${name}"`);
      setReassign(null); setReassignTo("");
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && detail.code === "reassign_required") {
        setReassign({ name, count: detail.count, options: detail.options || [] });
        setReassignTo("");
      } else {
        toast.error((detail && detail.message) || detail || "Could not remove vertical");
      }
    } finally { setBusy(false); }
  };

  // require explicit confirmation for global taxonomy changes (rename / remove)
  const requestRename = () => {
    const old = rename?.old; const nn = (rename?.value || "").trim();
    if (!nn || nn === old) { setRename(null); return; }
    if (taxRows.some((r) => r.vertical === nn)) { toast.error(`"${nn}" already exists`); return; }
    setConfirm({ type: "rename", name: old, to: nn });
  };
  const requestRemove = (name) => setConfirm({ type: "remove", name });
  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.type === "rename") await doRename();
    else if (c.type === "remove") await doRemove(c.name);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button variant="outline" size="sm" data-testid="pd-manage-verticals-open"><SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Manage verticals</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="pd-manage-verticals-dialog">
        <DialogHeader>
          <DialogTitle>Manage verticals — {project}</DialogTitle>
          <DialogDescription>
            Add, rename or remove verticals (shared across the whole app) and set the planned budget per vertical
            for this project. Spend stays auto-populated from invoices.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-[hsl(var(--surface-2))] px-3 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Add vertical</span>
          <Input value={newVert} onChange={(e) => setNewVert(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVertical(); } }}
            placeholder="e.g. Events" className="h-8 w-48" data-testid="pd-add-vertical-input" />
          <Button size="sm" variant="outline" className="h-8" disabled={busy || !newVert.trim()} onClick={addVertical} data-testid="pd-add-vertical-btn">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </div>

        <div className="table-scroll" style={{ maxHeight: "46vh" }}>
          <table className="w-full border-collapse text-sm" data-testid="pd-verticals-table">
            <thead><tr>
              <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Vertical</th>
              <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Spend (auto)</th>
              <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Planned budget (₹)</th>
              <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Actions</th>
            </tr></thead>
            <tbody>
              {taxRows.map((r, i) => (
                <tr key={r.vertical} className={"border-b last:border-0 " + (i % 2 ? "row-zebra" : "")} data-testid={`pd-vert-row-${slug(r.vertical)}`}>
                  <td className="td-sticky-left px-3 py-1.5 font-medium">
                    {rename && rename.old === r.vertical ? (
                      <span className="flex items-center gap-1">
                        <Input value={rename.value} autoFocus onChange={(e) => setRename({ ...rename, value: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") requestRename(); if (e.key === "Escape") setRename(null); }}
                          className="h-7 w-40" data-testid={`pd-rename-input-${slug(r.vertical)}`} />
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={busy} onClick={requestRename} data-testid={`pd-rename-confirm-${slug(r.vertical)}`}><Check className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setRename(null)}><X className="h-3.5 w-3.5" /></Button>
                      </span>
                    ) : r.vertical}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{money(r.spend)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Input type="number" min="0" value={plan[r.vertical] ?? "0"}
                      onChange={(e) => setPlan((s) => ({ ...s, [r.vertical]: e.target.value }))}
                      className="h-8 w-32 text-right tabular-nums" data-testid={`pd-plan-input-${slug(r.vertical)}`} />
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <span className="inline-flex gap-0.5">
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Rename" disabled={busy}
                        onClick={() => setRename({ old: r.vertical, value: r.vertical })} data-testid={`pd-rename-${slug(r.vertical)}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--danger))]" title="Remove" disabled={busy}
                        onClick={() => requestRemove(r.vertical)} data-testid={`pd-remove-${slug(r.vertical)}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  </td>
                </tr>
              ))}
              {extraRows.map((r) => (
                <tr key={r.vertical} className="border-b bg-[hsl(var(--surface-2))] last:border-0" data-testid={`pd-vert-extra-${slug(r.vertical)}`}>
                  <td className="td-sticky-left px-3 py-1.5 font-medium text-muted-foreground">{r.vertical} <span className="text-[10px]">(not in taxonomy)</span></td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{money(r.spend)}</td>
                  <td className="px-3 py-1.5 text-right text-[11px] text-muted-foreground">—</td>
                  <td className="px-3 py-1.5 text-right text-[11px] text-muted-foreground">rename in Data Manager</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="sm:justify-between">
          <span className="text-xs text-muted-foreground">{changed.length} unsaved budget change{changed.length === 1 ? "" : "s"}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={savePlanned} disabled={busy || changed.length === 0} data-testid="pd-save-planned">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save budgets
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* confirm global rename / remove */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && !busy && setConfirm(null)}>
        <AlertDialogContent data-testid="pd-vert-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
              {confirm?.type === "rename" ? "Rename vertical globally?" : "Remove vertical globally?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "rename" ? (
                <>Renaming <b>&ldquo;{confirm?.name}&rdquo;</b> to <b>&ldquo;{confirm?.to}&rdquo;</b> updates the shared taxonomy
                and re-tags every matching invoice &amp; planned budget across the whole app
                {vertCount(confirm?.name) > 0 ? <> ({vertCount(confirm?.name)} line item{vertCount(confirm?.name) === 1 ? "" : "s"} on this project).</> : "."}</>
              ) : (
                <>Removing <b>&ldquo;{confirm?.name}&rdquo;</b> takes it out of the shared taxonomy for all projects.
                {vertCount(confirm?.name) > 0 ? <> Its {vertCount(confirm?.name)} tagged invoice(s) will need to be reassigned first.</> : " It has no tagged invoices on this project."}</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pd-vert-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} data-testid="pd-vert-confirm-action"
              className={confirm?.type === "remove" ? "bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/90" : ""}>
              {confirm?.type === "rename" ? "Rename everywhere" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* reassign-before-delete */}
      <Dialog open={!!reassign} onOpenChange={(o) => !o && !busy && (setReassign(null), setReassignTo(""))}>
        <DialogContent className="max-w-md" data-testid="pd-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign invoices first</DialogTitle>
            <DialogDescription>{reassign?.count} invoice(s) are tagged "{reassign?.name}". Choose a vertical to move them to before removing it.</DialogDescription>
          </DialogHeader>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger data-testid="pd-reassign-select"><SelectValue placeholder="Select a vertical" /></SelectTrigger>
            <SelectContent>{(reassign?.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReassign(null); setReassignTo(""); }}>Cancel</Button>
            <Button variant="destructive" disabled={busy || !reassignTo} onClick={() => doRemove(reassign.name, reassignTo)} data-testid="pd-reassign-confirm">Reassign &amp; remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
